import React from 'react';
import { Box } from '@mui/material';
import { keyframes } from '@emotion/react';
import { useLocation } from 'react-router-dom';

const rotateCW  = keyframes`from{transform:rotate(0deg)}to{transform:rotate(360deg)}`;
const rotateCCW = keyframes`from{transform:rotate(0deg)}to{transform:rotate(-360deg)}`;
const pulseGlow = keyframes`
  0%,100%{opacity:0.18}
  50%     {opacity:0.32}
`;
const dotPulse = keyframes`
  0%,100%{box-shadow:0 0 3px 1px rgba(255,107,107,0.5)}
  50%     {box-shadow:0 0 8px 3px rgba(255,107,107,0.9),0 0 14px 1px rgba(255,217,61,0.3)}
`;

/* Pages that already have their own full hero graphic — skip ambient rings there */
const SKIP_ROUTES = ['/', '/services'];

export default function PageBackground() {
  const { pathname } = useLocation();
  if (SKIP_ROUTES.includes(pathname)) return null;

  return (
    <Box
      aria-hidden="true"
      sx={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 0,
        overflow: 'hidden',
      }}
    >
      {/* ── Top-right ring cluster ─────────────────────────────────── */}
      <Box sx={{ position: 'absolute', top: 80, right: -40 }}>
        {/* Outer dashed orbit */}
        <Box sx={{
          width: 220, height: 220,
          borderRadius: '50%',
          border: '1px dashed rgba(255,107,107,0.18)',
          animation: `${rotateCW} 28s linear infinite`,
          position: 'relative',
        }}>
          {[
            { top: '-4px', left: '50%', transform: 'translateX(-50%)' },
            { bottom: '-4px', left: '50%', transform: 'translateX(-50%)' },
            { left: '-4px', top: '50%', transform: 'translateY(-50%)' },
            { right: '-4px', top: '50%', transform: 'translateY(-50%)' },
          ].map((pos, i) => (
            <Box key={i} sx={{
              position: 'absolute', ...pos,
              width: 7, height: 7, borderRadius: '50%',
              background: 'radial-gradient(circle, #ff6b6b 0%, rgba(255,107,107,0.2) 100%)',
              animation: `${dotPulse} 2.4s ease-in-out infinite`,
              animationDelay: `${i * 0.6}s`,
            }} />
          ))}
        </Box>

        {/* Middle solid ring */}
        <Box sx={{
          position: 'absolute',
          top: '50%', left: '50%',
          width: 150, height: 150,
          ml: '-75px', mt: '-75px',
          borderRadius: '50%',
          border: '1px solid rgba(255,107,107,0.12)',
          animation: `${rotateCCW} 18s linear infinite`,
        }} />

        {/* Inner glow core */}
        <Box sx={{
          position: 'absolute',
          top: '50%', left: '50%',
          width: 70, height: 70,
          ml: '-35px', mt: '-35px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,107,107,0.12) 0%, transparent 70%)',
          animation: `${pulseGlow} 4s ease-in-out infinite`,
        }} />
      </Box>

      {/* ── Bottom-left ring cluster ───────────────────────────────── */}
      <Box sx={{ position: 'absolute', bottom: 60, left: -50 }}>
        <Box sx={{
          width: 160, height: 160,
          borderRadius: '50%',
          border: '1px dashed rgba(255,217,61,0.14)',
          animation: `${rotateCCW} 22s linear infinite`,
          position: 'relative',
        }}>
          {[
            { top: '-4px', left: '50%', transform: 'translateX(-50%)' },
            { bottom: '-4px', left: '50%', transform: 'translateX(-50%)' },
          ].map((pos, i) => (
            <Box key={i} sx={{
              position: 'absolute', ...pos,
              width: 6, height: 6, borderRadius: '50%',
              background: 'radial-gradient(circle, #ffd93d 0%, rgba(255,217,61,0.2) 100%)',
              animation: `${dotPulse} 3s ease-in-out infinite`,
              animationDelay: `${i * 1.2}s`,
            }} />
          ))}
        </Box>
        <Box sx={{
          position: 'absolute',
          top: '50%', left: '50%',
          width: 90, height: 90,
          ml: '-45px', mt: '-45px',
          borderRadius: '50%',
          border: '1px solid rgba(255,217,61,0.1)',
          animation: `${rotateCW} 14s linear infinite`,
        }} />
      </Box>

      {/* ── Ambient glow blobs ────────────────────────────────────── */}
      <Box sx={{
        position: 'absolute', top: '8%', right: '5%',
        width: 380, height: 380, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,107,107,0.06) 0%, transparent 70%)',
        animation: `${pulseGlow} 6s ease-in-out infinite`,
      }} />
      <Box sx={{
        position: 'absolute', top: '45%', left: '2%',
        width: 260, height: 260, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,217,61,0.05) 0%, transparent 70%)',
        animation: `${pulseGlow} 8s ease-in-out infinite`,
        animationDelay: '2s',
      }} />
      <Box sx={{
        position: 'absolute', bottom: '15%', right: '10%',
        width: 200, height: 200, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(78,204,196,0.04) 0%, transparent 70%)',
        animation: `${pulseGlow} 7s ease-in-out infinite`,
        animationDelay: '4s',
      }} />

      {/* ── Subtle top accent line ─────────────────────────────────── */}
      <Box sx={{
        position: 'absolute', top: 0, left: 0, right: 0,
        height: '2px',
        background: 'linear-gradient(90deg, transparent 0%, rgba(255,107,107,0.25) 30%, rgba(255,217,61,0.2) 60%, transparent 100%)',
      }} />
    </Box>
  );
}
